import React from "react";
import { Check, Maximize2, Plus } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { IDR } from "@/lib/format";
import { onImgError } from "@/components/gallery/imgError";

/** Tile border tells the client which bucket the photo is in — extra, included, or unpicked. */
const tileBorder = ({ selected, isExtra }) => {
  if (isExtra) return "border-[#D4AF37] border-dashed";
  if (selected) return "border-[#D4AF37]";
  return "border-transparent hover:border-[#3a3a3a]";
};

/** One grid tile: full-screen inspect button plus the corner select checkbox. */
export const PhotoTile = ({ photo, selected, isExtra, wouldCharge, extraPrice, onZoom, onToggleSelect }) => {
  const { t } = useI18n();

  return (
    <div className="mb-4 break-inside-avoid relative group">
      <button data-testid={`photo-open-${photo.file_id}`} onClick={() => onZoom(photo)}
        aria-label={`Inspect ${photo.name}`}
        className={`block w-full relative overflow-hidden border-2 transition-all duration-300 ${
          tileBorder({ selected, isExtra })
        }`}>
        <img src={photo.thumb} alt={photo.name} loading="lazy" decoding="async"
          srcSet={photo.thumb_2x ? `${photo.thumb} 1x, ${photo.thumb_2x} 2x` : undefined}
          onError={(e) => onImgError(e, photo)}
          className={`w-full transition-transform duration-500 ${selected ? "opacity-90" : "group-hover:scale-105"}`} />
        <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hidden sm:flex items-center justify-center">
          <Maximize2 size={22} className="text-[#F3F3F1]" />
        </span>
        {isExtra && (
          <span data-testid={`extra-badge-${photo.file_id}`}
            className="absolute bottom-0 left-0 right-0 bg-[#D4AF37] text-black text-[9px] tracking-[0.16em] uppercase py-1 text-center font-semibold">
            {t("extra")} · {IDR(extraPrice)}
          </span>
        )}
        {wouldCharge && (
          <span data-testid={`charge-hint-${photo.file_id}`}
            className="absolute bottom-2 right-2 glass border border-[#D4AF37]/50 text-[#D4AF37] px-2 py-1 text-[9px] tracking-[0.14em] uppercase flex items-center gap-1">
            <Plus size={10} /> {IDR(extraPrice)}
          </span>
        )}
      </button>
      <button data-testid={`photo-select-${photo.file_id}`}
        onClick={(e) => { e.stopPropagation(); onToggleSelect(photo.file_id); }}
        aria-pressed={selected} aria-label={`${selected ? "Deselect" : "Select"} ${photo.name}`}
        className="absolute top-0 left-0 w-14 h-14 flex items-start justify-start p-2">
        <span className={`w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center border transition-colors duration-300 ${
          selected ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "glass border-[#3a3a3a] text-transparent hover:border-[#D4AF37]"
        }`}>
          <Check size={18} strokeWidth={3} />
        </span>
      </button>
      <span className="mt-2 block font-mono-accent text-[10px] text-[#707070] truncate">{photo.name}</span>
    </div>
  );
};

/** Quota chips plus the bulk select / unselect pair. */
export const GridQuotaBar = ({ data, quota, quotaFull, baseCount, totalPicked, maxTotal, extraPrice,
  onSelectRemaining, onUnselectAll }) => {
  const { t } = useI18n();

  return (
    <>
      <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="quota-banner">
        <span className={`font-mono-accent text-[10px] tracking-[0.16em] uppercase border px-3 py-2 ${
          quotaFull ? "border-[#2A2A2A] text-[#707070]" : "border-[#D4AF37]/40 text-[#D4AF37]"
        }`} data-testid="quota-remaining">
          {quotaFull ? t("quota_used_up") : t("quota_left", { n: quota - baseCount })}
        </span>
        {data.allow_extra_edits && (
          <span className="font-mono-accent text-[10px] tracking-[0.16em] uppercase text-[#A8A8A8]"
            data-testid="extra-price-note">
            {t("then_each", { price: IDR(extraPrice) })}
          </span>
        )}
        {maxTotal > 0 && (
          <span className="font-mono-accent text-[10px] tracking-[0.16em] uppercase text-[#5A5A5A]"
            data-testid="max-total-note">
            {t("max_total", { n: maxTotal })}
          </span>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="bulk-select-bar">
        <button type="button" data-testid="bulk-select-all-btn" onClick={onSelectRemaining}
          disabled={quotaFull}
          className="border border-[#D4AF37]/40 text-[#D4AF37] px-4 min-h-[44px] text-[10px] tracking-[0.16em] uppercase hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40 disabled:hover:bg-transparent">
          {t("select_all_remaining")}
        </button>
        <button type="button" data-testid="bulk-unselect-all-btn" onClick={onUnselectAll}
          disabled={totalPicked === 0}
          className="border border-[#2A2A2A] text-[#A8A8A8] px-4 min-h-[44px] text-[10px] tracking-[0.16em] uppercase hover:text-[#F3F3F1] transition-colors disabled:opacity-40">
          {t("unselect_all")}
        </button>
      </div>
    </>
  );
};
